<template>
  <div>
    <topsearch title="Sunucu Durumu" />
    <div class="flex">
      <div class="w-72 border-r h-full" style="min-width:217px">
        <adminSidebar />
      </div>
      <div class="info-content md:p-4 p-2">
        <div v-if="info" class="text-xs text-gray-500 mb-3 text-right">
          Son ölçüm {{ clock(info.collected_at) }} · {{ pollSeconds }} sn'de bir
          yenilenir
        </div>

        <div v-if="forbidden" class="p-3 rounded-lg bg-red-100 text-red-700">
          Bu sayfayı görüntüleme yetkiniz yok.
        </div>
        <div
          v-else-if="!info && error"
          class="p-3 rounded-lg bg-red-100 text-red-700"
        >
          Sunucu bilgileri alınamadı. Yeniden deneniyor...
        </div>
        <div v-else-if="!info" class="text-gray-500 p-3">Yükleniyor...</div>

        <template v-else>
          <!-- Eski veriyi güncel sanmamak için -->
          <div
            v-if="error"
            class="mb-3 p-3 rounded-lg bg-red-100 text-red-700 text-sm"
          >
            Güncellenemiyor. Ekrandaki değerler
            {{ clock(info.collected_at) }} ölçümüne ait.
          </div>

          <div v-if="alerts.length" class="mb-3 bg-white border rounded-lg p-3">
            <div class="text-sm font-medium mb-1">Dikkat gerektirenler</div>
            <div
              v-for="alert in alerts"
              :key="alert.key"
              class="flex items-start text-sm py-1"
            >
              <span
                class="status-dot"
                :class="'status-' + alert.level"
                aria-hidden="true"
              ></span>
              <span class="status-label">{{ levelLabel(alert.level) }}</span>
              <span class="text-gray-700">{{ alert.text }}</span>
            </div>
          </div>

          <div
            class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-3"
          >
            <div
              v-for="tile in tiles"
              :key="tile.key"
              class="bg-white border rounded-lg p-3"
            >
              <div class="flex justify-between text-sm text-gray-600">
                <span>{{ tile.label }}</span>
                <span v-if="tile.level !== 'ok'" class="text-xs">{{
                  levelLabel(tile.level)
                }}</span>
              </div>
              <div class="text-2xl font-semibold mt-1">{{ tile.value }}</div>
              <div
                v-if="tile.percent !== null"
                class="meter"
                role="presentation"
              >
                <div
                  class="meter-fill"
                  :class="'status-' + tile.level"
                  :style="{ width: Math.min(tile.percent, 100) + '%' }"
                ></div>
              </div>
              <div class="text-xs text-gray-500 mt-2">{{ tile.detail }}</div>
            </div>
          </div>

          <div class="bg-white border rounded-lg p-3 mb-3">
            <div class="text-sm font-medium">CPU ve RAM kullanımı (%)</div>
            <div class="text-xs text-gray-500 mb-1">
              Son {{ historyMinutes }} dakika. Sadece bu sayfa açıkken toplanır,
              yenileyince sıfırlanır.
            </div>
            <apexchart
              v-if="history.length > 1"
              type="line"
              height="240"
              :options="chartOptions"
              :series="chartSeries"
            />
            <div v-else class="text-sm text-gray-500 py-8 text-center">
              Veri toplanıyor...
            </div>
          </div>

          <div class="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium mb-2">
                En çok kaynak kullanan süreçler
              </div>
              <table v-if="info.top_processes" class="w-full text-sm">
                <thead>
                  <tr class="text-xs text-gray-500 text-left">
                    <th class="font-normal pb-1">Süreç</th>
                    <th class="font-normal pb-1 text-right">Adet</th>
                    <th class="font-normal pb-1 text-right">CPU</th>
                    <th class="font-normal pb-1 text-right">RAM (yaklaşık)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="process in info.top_processes"
                    :key="process.name"
                    class="border-t"
                  >
                    <td class="py-1 pr-2 break-all">{{ process.name }}</td>
                    <td class="py-1 text-right tabular">{{ process.count }}</td>
                    <td class="py-1 text-right tabular">
                      %{{ number(process.cpu_percent) }}
                    </td>
                    <td class="py-1 text-right tabular">
                      {{ bytes(process.rss_bytes) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-sm text-gray-500">Ölçülemedi</div>
              <div v-if="info.top_processes" class="text-xs text-gray-500 mt-2">
                CPU, sunucunun toplam kapasitesine oranla. RAM'de süreçlerin
                paylaştığı bellek tekrar sayılır, gerçek kullanımdan yüksek
                görünebilir.
              </div>
            </div>

            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium mb-2">Diskler</div>
              <template v-if="info.disks">
                <div v-for="disk in info.disks" :key="disk.mount" class="mb-3">
                  <div class="flex justify-between text-sm">
                    <span class="break-all pr-2">{{ disk.mount }}</span>
                    <span class="tabular whitespace-nowrap">
                      {{ bytes(disk.used_bytes) }} /
                      {{ bytes(disk.total_bytes) }}
                    </span>
                  </div>
                  <div class="meter" role="presentation">
                    <div
                      class="meter-fill"
                      :class="'status-' + diskLevel(disk)"
                      :style="{ width: diskPercent(disk) + '%' }"
                    ></div>
                  </div>
                </div>
              </template>
              <div v-else class="text-sm text-gray-500 mb-3">Ölçülemedi</div>
              <metric-rows :rows="diskIoRows" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <div
              v-for="panel in panels"
              :key="panel.title"
              class="bg-white border rounded-lg p-3"
            >
              <div class="text-sm font-medium mb-2">{{ panel.title }}</div>
              <div v-if="panel.empty" class="text-sm text-gray-500">
                {{ panel.empty }}
              </div>
              <metric-rows v-else :rows="panel.rows" />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import SidebarVue from "../components/Ui/Admin/Sidebar.vue";
import http from "../services/http";

// Kategorik sıra (doğrulandı: CVD / kontrast): 1 mavi, 2 turuncu.
const SERIES_CPU = "#2a78d6";
const SERIES_RAM = "#eb6834";
const GRID = "#eceae5";
const TEXT_MUTED = "#6b6a66";

const POLL_MS = 5000;
const HISTORY_MINUTES = 15;
const HISTORY_POINTS = (HISTORY_MINUTES * 60 * 1000) / POLL_MS;

const LEVEL_LABELS = { warning: "Yüksek", critical: "Kritik", ok: "Normal" };

function level(value, warning, critical) {
  if (value >= critical) return "critical";
  return value >= warning ? "warning" : "ok";
}

const MetricRows = {
  functional: true,
  props: { rows: { type: Array, required: true } },
  render(h, { props }) {
    return h(
      "div",
      props.rows.map(row =>
        h(
          "div",
          {
            class: "flex justify-between text-sm py-1 border-t first:border-t-0"
          },
          [
            h("span", { class: "text-gray-600 pr-2" }, row.label),
            h("span", { class: "tabular text-right" }, row.value)
          ]
        )
      )
    );
  }
};

export default {
  name: "AdminInfo",
  components: {
    adminSidebar: SidebarVue,
    apexchart: VueApexCharts,
    MetricRows
  },
  data() {
    return {
      info: null,
      error: false,
      forbidden: false,
      history: [],
      pollTimer: null,
      destroyed: false,
      pollSeconds: POLL_MS / 1000,
      historyMinutes: HISTORY_MINUTES
    };
  },
  computed: {
    memoryPercent() {
      const memory = this.info.memory;
      return memory ? (memory.used_bytes / memory.total_bytes) * 100 : null;
    },
    // Load, çekirdek sayısına göre anlam kazanır (1 = kapasite doldu, kuyruk başlıyor).
    loadPerCore() {
      const { load, cpu } = this.info;
      return load && cpu && cpu.cores ? load.one / cpu.cores : null;
    },
    fullestDisk() {
      return this.info.disks ? this.info.disks[0] : null;
    },
    tiles() {
      const { cpu, load, memory } = this.info;
      const disk = this.fullestDisk;
      return [
        {
          key: "cpu",
          label: "CPU",
          value: cpu ? "%" + this.number(cpu.usage_percent) : "Ölçülemedi",
          percent: cpu ? cpu.usage_percent : null,
          level: cpu ? level(cpu.usage_percent, 75, 90) : "ok",
          detail: cpu
            ? cpu.cores +
              " çekirdek · disk bekleme %" +
              this.number(cpu.iowait_percent) +
              " · steal %" +
              this.number(cpu.steal_percent)
            : ""
        },
        {
          key: "memory",
          label: "RAM",
          value: memory ? "%" + this.number(this.memoryPercent) : "Ölçülemedi",
          percent: memory ? this.memoryPercent : null,
          level: memory ? level(this.memoryPercent, 80, 90) : "ok",
          detail: memory
            ? this.bytes(memory.used_bytes) +
              " / " +
              this.bytes(memory.total_bytes) +
              this.swapLabel(memory)
            : ""
        },
        {
          key: "load",
          label: "Sistem yükü (1 dk)",
          value: load ? this.number(load.one) : "Ölçülemedi",
          percent: this.loadPerCore === null ? null : this.loadPerCore * 100,
          level:
            this.loadPerCore === null ? "ok" : level(this.loadPerCore, 1, 2),
          detail: load
            ? "5 dk " +
              this.number(load.five) +
              " · 15 dk " +
              this.number(load.fifteen) +
              (this.info.platform.uptime_seconds !== null
                ? " · açık " + this.duration(this.info.platform.uptime_seconds)
                : "")
            : ""
        },
        {
          key: "disk",
          label: "En dolu disk",
          value: disk
            ? "%" + this.number(this.diskPercent(disk))
            : "Ölçülemedi",
          percent: disk ? this.diskPercent(disk) : null,
          level: disk ? this.diskLevel(disk) : "ok",
          detail: disk
            ? disk.mount +
              " · " +
              this.bytes(disk.total_bytes - disk.used_bytes) +
              " boş"
            : ""
        }
      ];
    },
    alerts() {
      const { cpu, memory, php, mysql, redis } = this.info;
      const alerts = [];
      const add = (key, alertLevel, text) => {
        if (alertLevel !== "ok") alerts.push({ key, level: alertLevel, text });
      };

      if (cpu) {
        add(
          "cpu",
          level(cpu.usage_percent, 75, 90),
          "CPU kullanımı %" + this.number(cpu.usage_percent)
        );
        add(
          "iowait",
          level(cpu.iowait_percent, 20, 40),
          "CPU zamanının %" +
            this.number(cpu.iowait_percent) +
            " kadarı disk beklemesinde: disk yetişemiyor olabilir"
        );
        add(
          "steal",
          level(cpu.steal_percent, 10, 25),
          "Steal %" +
            this.number(cpu.steal_percent) +
            ": sanal sunucunun CPU'sunu aynı fiziksel makinedeki başka sunucular alıyor"
        );
      }
      if (this.loadPerCore !== null) {
        add(
          "load",
          level(this.loadPerCore, 1, 2),
          "Sistem yükü " +
            this.number(this.info.load.one) +
            ", çekirdek sayısı " +
            cpu.cores +
            ": işler sıra bekliyor"
        );
      }
      if (memory) {
        add(
          "memory",
          level(this.memoryPercent, 80, 90),
          "RAM kullanımı %" + this.number(this.memoryPercent)
        );
        if (memory.swap_total_bytes > 0) {
          const swap = (memory.swap_used_bytes / memory.swap_total_bytes) * 100;
          add(
            "swap",
            level(swap, 50, 80),
            "Swap kullanımı %" + this.number(swap) + ": RAM yetmiyor olabilir"
          );
        }
      }
      (this.info.disks || []).forEach(disk => {
        add(
          "disk-" + disk.mount,
          this.diskLevel(disk),
          disk.mount +
            " diski %" +
            this.number(this.diskPercent(disk)) +
            " dolu"
        );
      });
      if (php.fpm && php.fpm.listen_queue > 0) {
        add(
          "fpm-queue",
          "critical",
          php.fpm.listen_queue + " istek boş PHP-FPM süreci bekliyor"
        );
      }
      if (php.fpm && php.fpm.max_children_reached > 0) {
        add(
          "fpm-max",
          "warning",
          "PHP-FPM, başlatıldığından beri " +
            php.fpm.max_children_reached +
            " kez süreç sınırına (max_children) dayandı"
        );
      }
      if (mysql) {
        if (mysql.max_connections > 0) {
          const percent = (mysql.connections / mysql.max_connections) * 100;
          add(
            "mysql-conn",
            level(percent, 75, 90),
            "MySQL bağlantıları " +
              mysql.connections +
              " / " +
              mysql.max_connections
          );
        }
        if (mysql.long_queries > 0) {
          add(
            "mysql-long",
            "warning",
            mysql.long_queries +
              " MySQL sorgusu 5 saniyeden uzun sürüyor (en uzunu " +
              mysql.longest_query_seconds +
              " sn)"
          );
        }
      } else {
        add("mysql", "critical", "MySQL durumu okunamadı");
      }
      if (!redis.available) {
        add("redis", "critical", "Redis'e bağlanılamadı");
      }
      return alerts.sort((a, b) =>
        a.level === b.level ? 0 : a.level === "critical" ? -1 : 1
      );
    },
    diskIoRows() {
      const io = this.info.disk_io;
      return [
        {
          label: "Okuma",
          value: io ? this.rate(io.read_bytes_per_second) : "Ölçülemedi"
        },
        {
          label: "Yazma",
          value: io ? this.rate(io.write_bytes_per_second) : "Ölçülemedi"
        },
        {
          label: "En meşgul disk (doluluk değil, çalışma süresi)",
          value: io ? "%" + this.number(io.busy_percent) : "Ölçülemedi"
        }
      ];
    },
    panels() {
      const {
        php,
        mysql,
        redis,
        network,
        sockets,
        workers,
        platform,
        load
      } = this.info;
      return [
        {
          title: "MySQL",
          empty: mysql ? null : "Okunamadı",
          rows: mysql
            ? [
                {
                  label: "Bağlantı",
                  value: mysql.connections + " / " + mysql.max_connections
                },
                {
                  label: "En yüksek bağlantı",
                  value: mysql.max_used_connections
                },
                { label: "Çalışan sorgu", value: mysql.running_threads },
                {
                  label: "Sorgu / sn",
                  value:
                    mysql.queries_per_second === null
                      ? "-"
                      : this.number(mysql.queries_per_second)
                },
                {
                  label: "5 sn'den uzun sorgu",
                  value: mysql.long_queries === null ? "-" : mysql.long_queries
                },
                { label: "Kilit bekleyen", value: mysql.row_lock_waits },
                {
                  label: "Yavaş sorgu (toplam)",
                  value: this.number(mysql.slow_queries_total)
                },
                {
                  label: "Buffer pool isabet",
                  value:
                    mysql.buffer_pool_hit_percent === null
                      ? "-"
                      : "%" + this.number(mysql.buffer_pool_hit_percent)
                },
                { label: "Sürüm", value: mysql.version },
                {
                  label: "Açık kalma",
                  value: this.duration(mysql.uptime_seconds)
                }
              ]
            : []
        },
        {
          title: "PHP",
          rows: [
            { label: "Sürüm", value: php.version + " (" + php.sapi + ")" },
            { label: "Bellek sınırı", value: php.memory_limit },
            ...(php.fpm
              ? [
                  { label: "FPM havuzu", value: php.fpm.pool },
                  {
                    label: "Aktif / boşta",
                    value: php.fpm.active + " / " + php.fpm.idle
                  },
                  { label: "Bekleyen istek", value: php.fpm.listen_queue },
                  {
                    label: "Süreç sınırına dayanma",
                    value: php.fpm.max_children_reached
                  },
                  {
                    label: "Yavaş istek",
                    value: this.number(php.fpm.slow_requests)
                  }
                ]
              : [{ label: "PHP-FPM", value: "Ölçülemedi" }]),
            ...(php.opcache
              ? [
                  {
                    label: "OPcache isabet",
                    value: php.opcache.enabled
                      ? "%" + this.number(php.opcache.hit_rate_percent)
                      : "Kapalı"
                  },
                  {
                    label: "OPcache bellek",
                    value:
                      this.bytes(php.opcache.used_bytes) +
                      " / " +
                      this.bytes(php.opcache.total_bytes)
                  }
                ]
              : [{ label: "OPcache", value: "Kapalı" }])
          ]
        },
        {
          title: "Redis",
          empty: redis.available ? null : "Bağlanılamadı",
          rows: redis.available
            ? [
                {
                  label: "Bellek",
                  value:
                    this.bytes(redis.used_memory_bytes) +
                    (redis.max_memory_bytes
                      ? " / " + this.bytes(redis.max_memory_bytes)
                      : "")
                },
                { label: "İstemci", value: redis.clients },
                { label: "Bekleyen istemci", value: redis.blocked_clients },
                {
                  label: "İşlem / sn",
                  value: this.number(redis.ops_per_second)
                },
                {
                  label: "İsabet oranı",
                  value:
                    redis.hit_rate_percent === null
                      ? "-"
                      : "%" + this.number(redis.hit_rate_percent)
                },
                {
                  label: "Bellekten atılan anahtar",
                  value: this.number(redis.evicted_keys)
                },
                { label: "Sürüm", value: redis.version }
              ]
            : []
        },
        {
          title: "Ağ",
          rows: [
            {
              label: "Gelen",
              value: network
                ? this.rate(network.received_bytes_per_second)
                : "Ölçülemedi"
            },
            {
              label: "Giden",
              value: network
                ? this.rate(network.sent_bytes_per_second)
                : "Ölçülemedi"
            },
            {
              label: "Açık TCP bağlantısı",
              value: sockets ? this.number(sockets.tcp_in_use) : "Ölçülemedi"
            },
            {
              label: "TIME_WAIT",
              value: sockets ? this.number(sockets.tcp_time_wait) : "Ölçülemedi"
            }
          ]
        },
        {
          title: "Arka plan servisleri",
          empty: workers ? null : "Ölçülemedi",
          rows: workers
            ? Object.keys(workers).map(name => ({
                label: name,
                value:
                  workers[name] > 0
                    ? "Çalışıyor (" + workers[name] + ")"
                    : "Çalışmıyor"
              }))
            : []
        },
        {
          title: "Sistem",
          rows: [
            { label: "İşletim sistemi", value: platform.os },
            { label: "Çekirdek", value: platform.kernel },
            {
              label: "İşlemci",
              value: (this.info.cpu && this.info.cpu.model) || "-"
            },
            {
              label: "Açık kalma",
              value:
                platform.uptime_seconds === null
                  ? "Ölçülemedi"
                  : this.duration(platform.uptime_seconds)
            },
            {
              label: "Toplam görev",
              value:
                load && load.total_tasks !== null
                  ? this.number(load.total_tasks)
                  : "-"
            }
          ]
        }
      ];
    },
    chartSeries() {
      const point = key => this.history.map(item => [item.at, item[key]]);
      return [
        { name: "CPU", data: point("cpu") },
        { name: "RAM", data: point("memory") }
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: "inherit"
        },
        colors: [SERIES_CPU, SERIES_RAM],
        stroke: { width: 2, curve: "straight" },
        markers: { size: 0, hover: { size: 5 } },
        dataLabels: { enabled: false },
        grid: { borderColor: GRID, strokeDashArray: 0 },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "left",
          labels: { colors: TEXT_MUTED }
        },
        xaxis: {
          type: "datetime",
          tickAmount: 6,
          labels: {
            datetimeUTC: false,
            format: "HH:mm:ss",
            style: { colors: TEXT_MUTED }
          },
          axisBorder: { color: GRID },
          axisTicks: { color: GRID },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: {
            style: { colors: TEXT_MUTED },
            formatter: value => "%" + Math.round(value)
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: { format: "HH:mm:ss" },
          y: {
            formatter: value =>
              value === null ? "-" : "%" + this.number(value)
          }
        }
      };
    }
  },
  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.load();
  },
  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.pollTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    async load() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      try {
        const { data } = await http.getSystemInfo();
        if (this.destroyed) return;
        this.info = data;
        this.error = false;
        this.remember(data);
      } catch (e) {
        if (this.destroyed) return;
        const status = e.response && e.response.status;
        if (status === 401 || status === 403) {
          this.forbidden = true;
          return;
        }
        this.error = true;
      }
      this.schedule();
    },
    // Bir sonraki istek öncekinin bitişinden sayılır: yavaş sunucuda istekler üst üste binmez.
    schedule() {
      if (!document.hidden && !this.destroyed) {
        this.pollTimer = setTimeout(this.load, POLL_MS);
      }
    },
    // Görünmeyen sekme sunucuya boşuna yük bindirmesin.
    onVisibilityChange() {
      if (document.hidden) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      } else if (!this.pollTimer && !this.forbidden) {
        this.load();
      }
    },
    remember(data) {
      const at = data.collected_at * 1000;
      const last = this.history[this.history.length - 1];
      // Sunucudaki kısa önbellek aynı ölçümü tekrar döndürebilir.
      if (last && last.at === at) return;
      const memory = data.memory;
      this.history.push({
        at,
        cpu: data.cpu ? data.cpu.usage_percent : null,
        memory: memory
          ? Math.round((memory.used_bytes / memory.total_bytes) * 1000) / 10
          : null
      });
      if (this.history.length > HISTORY_POINTS) this.history.shift();
    },
    levelLabel(value) {
      return LEVEL_LABELS[value];
    },
    diskPercent(disk) {
      return Math.round((disk.used_bytes / disk.total_bytes) * 1000) / 10;
    },
    diskLevel(disk) {
      return level(this.diskPercent(disk), 80, 90);
    },
    swapLabel(memory) {
      return memory.swap_total_bytes > 0
        ? " · swap " + this.bytes(memory.swap_used_bytes)
        : " · swap yok";
    },
    number(value) {
      return Number(value).toLocaleString("tr-TR", {
        maximumFractionDigits: 1
      });
    },
    bytes(value) {
      const units = ["B", "KB", "MB", "GB", "TB"];
      let size = value;
      let unit = 0;
      while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit++;
      }
      return this.number(size) + " " + units[unit];
    },
    rate(value) {
      return this.bytes(value) + "/sn";
    },
    duration(seconds) {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      if (days) return days + " gün " + hours + " saat";
      const minutes = Math.floor((seconds % 3600) / 60);
      return hours ? hours + " saat " + minutes + " dk" : minutes + " dk";
    },
    clock(seconds) {
      return new Date(seconds * 1000).toLocaleTimeString("tr-TR");
    }
  }
};
</script>

<style scoped>
/* Genişliği içerik değil kalan alan belirlesin: grafik SVG'sinin sabit genişliği
   uygulama kabuğunu ekran dışına itiyordu. */
.info-content {
  flex: 1 1 0;
  width: 0;
  min-width: 0;
}

.meter {
  height: 6px;
  margin-top: 8px;
  border-radius: 3px;
  background: #e8eff9;
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  border-radius: 3px;
}

.status-ok {
  background: #2a78d6;
}

.status-warning {
  background: #fab219;
}

.status-critical {
  background: #d03b3b;
}

.status-dot {
  flex: none;
  width: 8px;
  height: 8px;
  margin: 6px 8px 0 0;
  border-radius: 50%;
}

.status-label {
  flex: none;
  width: 52px;
  font-weight: 500;
}

.tabular {
  font-variant-numeric: tabular-nums;
}
</style>
